import { useEffect, useMemo, useState } from 'react'
import { Loader2, Palette, Trash2, X } from 'lucide-react'
import {
  WORK_TYPE_CATEGORY_COLORS,
  type WorkTypeCategory,
  WorkTypeCategoriesService,
} from '../../services/workTypeCategoriesService'

interface EditWorkTypeCategoriesModalProps {
  isOpen: boolean
  onClose: () => void
  categories: WorkTypeCategory[]
  /** categoryId → count of work types (null key not used; uncategorised omitted). */
  usageCounts: Record<string, number>
  /** When set, only these category ids are listed (team filter active). */
  visibleCategoryIds?: string[] | null
  /** Optional label shown when filtered by team. */
  filterHint?: string | null
  onChanged: () => Promise<void> | void
}

type Draft = {
  id: string
  name: string
  color: string
}

type DeletePrompt = {
  categoryId: string
  name: string
  count: number
  transferTo: string | 'uncategorised'
}

export function EditWorkTypeCategoriesModal({
  isOpen,
  onClose,
  categories,
  usageCounts,
  visibleCategoryIds = null,
  filterHint = null,
  onChanged,
}: EditWorkTypeCategoriesModalProps) {
  const service = useMemo(() => WorkTypeCategoriesService.getInstance(), [])
  const [drafts, setDrafts] = useState<Draft[]>([])
  const [savingId, setSavingId] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [deletePrompt, setDeletePrompt] = useState<DeletePrompt | null>(null)
  const [deleting, setDeleting] = useState(false)

  const visibleCategories = useMemo(() => {
    if (!visibleCategoryIds) return categories
    const allowed = new Set(visibleCategoryIds)
    return categories.filter((c) => allowed.has(c.id))
  }, [categories, visibleCategoryIds])

  useEffect(() => {
    if (!isOpen) return
    setDrafts(
      visibleCategories.map((c) => ({
        id: c.id,
        name: c.name,
        color: c.color,
      }))
    )
    setError('')
    setDeletePrompt(null)
  }, [isOpen, visibleCategories])

  if (!isOpen) return null

  const updateDraft = (id: string, patch: Partial<Draft>) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)))
  }

  const handleSaveRow = async (draft: Draft) => {
    const original = categories.find((c) => c.id === draft.id)
    if (!original) return
    const name = draft.name.trim()
    if (!name) {
      setError('Category name is required')
      return
    }
    if (name === original.name && draft.color === original.color) return

    try {
      setSavingId(draft.id)
      setError('')
      await service.updateCategory(draft.id, { name, color: draft.color })
      await onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update category')
    } finally {
      setSavingId(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deletePrompt) return
    try {
      setDeleting(true)
      setError('')
      const transferTo =
        deletePrompt.transferTo === 'uncategorised' ? null : deletePrompt.transferTo
      await service.deleteCategory(deletePrompt.categoryId, transferTo)
      setDeletePrompt(null)
      await onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete category')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-gray-900">Edit categories</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {filterHint && (
            <p className="text-xs text-gray-500">
              Showing categories used by {filterHint}. Clear the team filter to edit all
              categories.
            </p>
          )}
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          {deletePrompt ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
              <p className="text-sm text-amber-900">
                Delete <span className="font-semibold">{deletePrompt.name}</span>?
                {deletePrompt.count > 0
                  ? ` ${deletePrompt.count} work type${deletePrompt.count === 1 ? '' : 's'} use this category.`
                  : ''}
              </p>
              {deletePrompt.count > 0 && (
                <div>
                  <label className="block text-sm font-medium text-amber-900 mb-1">
                    Move work types to
                  </label>
                  <select
                    value={deletePrompt.transferTo}
                    onChange={(e) =>
                      setDeletePrompt({
                        ...deletePrompt,
                        transferTo: e.target.value,
                      })
                    }
                    className="w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="uncategorised">Leave Uncategorised</option>
                    {categories
                      .filter((c) => c.id !== deletePrompt.categoryId)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeletePrompt(null)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-white"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Delete category
                </button>
              </div>
            </div>
          ) : drafts.length === 0 ? (
            <p className="text-sm text-gray-500">
              {visibleCategoryIds
                ? 'No categories for the selected team filter.'
                : 'No categories yet.'}
            </p>
          ) : (
            <ul className="space-y-4">
              {drafts.map((draft) => {
                const count = usageCounts[draft.id] || 0
                const dirty =
                  categories.find((c) => c.id === draft.id)?.name !== draft.name.trim() ||
                  categories.find((c) => c.id === draft.id)?.color !== draft.color
                return (
                  <li
                    key={draft.id}
                    className="rounded-lg border border-gray-200 p-3 space-y-3"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className="mt-2 w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: draft.color }}
                      />
                      <div className="flex-1 min-w-0 space-y-2">
                        <input
                          type="text"
                          value={draft.name}
                          onChange={(e) => updateDraft(draft.id, { name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        />
                        <div className="flex flex-wrap gap-1.5">
                          {WORK_TYPE_CATEGORY_COLORS.map((color) => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => updateDraft(draft.id, { color })}
                              className={`w-6 h-6 rounded-full transition-all ${
                                draft.color === color
                                  ? 'ring-2 ring-offset-1 ring-gray-400 scale-110'
                                  : 'hover:scale-110'
                              }`}
                              style={{ backgroundColor: color }}
                              title={color}
                            />
                          ))}
                        </div>
                        <p className="text-xs text-gray-400">
                          {count} work type{count === 1 ? '' : 's'}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleSaveRow(draft)}
                          disabled={!dirty || savingId === draft.id}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40"
                        >
                          {savingId === draft.id ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setDeletePrompt({
                              categoryId: draft.id,
                              name: draft.name,
                              count,
                              transferTo: 'uncategorised',
                            })
                          }
                          className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="flex justify-end p-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 text-sm"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
